import React, { useState, useEffect, useRef } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';

function fmtPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '').replace(/^1/, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

export default function NewTicketModal({ theme, onClose, onCreated, allStatuses, currentUser = null }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [matches, setMatches] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searching, setSearching] = useState(false);

  const [bikeDescription, setBikeDescription] = useState('');
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [serial, setSerial] = useState('');
  // One of the selected customer's earlier bikes (7 Oct), as Lightspeed offers
  // once a customer is on a new ticket. '' = a new bike, described here.
  const [customerBikes, setCustomerBikes] = useState(null);
  const [bikeId, setBikeId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [techId, setTechId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/tickets/employees/list`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setEmployees(data); })
      .catch(() => {});
  }, []);

  // Defaults: status = Open, tech = logged-in user's linked LS employee
  useEffect(() => {
    if (!statusId && allStatuses?.length) {
      const open = allStatuses.find(s => s.name.toLowerCase() === 'open');
      if (open) setStatusId(open.id);
    }
  }, [allStatuses]);
  useEffect(() => {
    if (!techId && currentUser?.employee_id) setTechId(String(currentUser.employee_id));
  }, [currentUser]);

  // Live customer search — name and/or phone, debounced 300ms
  useEffect(() => {
    if (selectedCustomer) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const nameQ = `${firstName} ${lastName}`.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    if (nameQ.length < 2 && phoneDigits.length < 4) { setMatches([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const queries = [];
        if (nameQ.length >= 2) queries.push(fetch(`${API}/api/customers?q=${encodeURIComponent(nameQ)}`, { credentials:'include', headers: authHeaders() }).then(r => r.json()).catch(() => []));
        if (phoneDigits.length >= 4) queries.push(fetch(`${API}/api/customers?q=${encodeURIComponent(phoneDigits)}`, { credentials:'include', headers: authHeaders() }).then(r => r.json()).catch(() => []));
        const resultSets = await Promise.all(queries);
        const seen = new Set(); const merged = [];
        resultSets.flat().forEach(c => {
          if (c?.id && !seen.has(c.id)) { seen.add(c.id); merged.push(c); }
        });
        setMatches(merged.slice(0, 6));
      } catch (_) { setMatches([]); }
      setSearching(false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [firstName, lastName, phone, selectedCustomer]);

  const selectMatch = (c) => {
    setSelectedCustomer(c);
    setFirstName(c.firstName || '');
    setLastName(c.lastName || '');
    setPhone(c.phone || '');
    setEmail(c.email || '');
    setMatches([]);
  };
  const clearSelection = () => setSelectedCustomer(null);

  // The selected customer's bikes. A bike picked for one customer never rides
  // along to another: changing the customer clears it.
  const customerId = selectedCustomer?.id ? String(selectedCustomer.id) : '';
  useEffect(() => {
    setBikeId('');
    setCustomerBikes(null);
    if (!customerId) return;
    let live = true;
    fetch(`${API}/api/tickets/customer-bikes/${customerId}`, { credentials:'include', headers: authHeaders() })
      .then(r => r.ok ? r.json() : { bikes: [] })
      .then(d => { if (live) setCustomerBikes(Array.isArray(d.bikes) ? d.bikes : []); })
      .catch(() => { if (live) setCustomerBikes([]); });
    return () => { live = false; };
  }, [customerId]);
  const pickedBike = (customerBikes || []).find(b => b.id === bikeId) || null;
  const pickBike = (id) => {
    setBikeId(id);
    const b = (customerBikes || []).find(x => x.id === id);
    setBikeDescription(b ? b.description : '');
    setColor(b ? b.color : '');
    setSize(b ? b.size : '');
    setSerial(b ? b.serial : '');
  };

  const canCreate = (pickedBike || bikeDescription.trim()) && (selectedCustomer || firstName.trim());

  const handleCreate = async () => {
    if (!canCreate || saving) return;
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/tickets/create`, {
        method:'POST', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({
          customerId: selectedCustomer?.id || null,
          firstName, lastName, phone, email,
          bikeDescription, color, size, serial,
          serializedId: pickedBike && selectedCustomer ? pickedBike.id : null,
          workorderStatusID: statusId || null,
          employeeID: techId || null,
          dueDate: dueDate || null,
          internalNote,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create ticket');
      if (onCreated) onCreated(data);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const labelStyle = { fontSize:10, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 };
  const inputStyle = {
    width:'100%', background: isDark ? '#0f1724' : '#fff',
    border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:6,
    padding:'8px 10px', color:T.text, fontSize:13, outline:'none', boxSizing:'border-box',
    fontFamily:'Arial, sans-serif',
  };
  const sectionLabel = { fontSize:10, letterSpacing:'0.1em', color: isDark ? '#5a6a8a' : '#999', fontWeight:600, marginBottom:10, textTransform:'uppercase' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', overflowY:'auto', padding:'24px 16px' }}>
      <div style={{ width:'100%', maxWidth:760, background:T.bg, borderRadius:14, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,0.5)', marginBottom:24 }}>

        {/* Header */}
        <div style={{ padding:'16px 22px', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:T.text }}>New Service Ticket</div>
            <div style={{ fontSize:12, color:T.textSub }}>Creates the workorder in Lightspeed</div>
          </div>
          <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${T.border}`, borderRadius:6, color:T.textSub, cursor:'pointer', padding:'5px 12px', fontSize:13 }}>✕</button>
        </div>

        {/* Section 1 — customer */}
        <div style={{ padding:'16px 22px' }}>
          <div style={sectionLabel}>1 · Find or create customer</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 220px', gap:14 }}>
            <div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                <div>
                  <div style={labelStyle}>First Name</div>
                  <input value={firstName} onChange={e => { setFirstName(e.target.value); clearSelection(); }} style={inputStyle} autoFocus />
                </div>
                <div>
                  <div style={labelStyle}>Last Name</div>
                  <input value={lastName} onChange={e => { setLastName(e.target.value); clearSelection(); }} style={inputStyle} />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div>
                  <div style={labelStyle}>Mobile Phone</div>
                  <input value={phone} onChange={e => { setPhone(e.target.value); clearSelection(); }} placeholder="(201) 555-1234" style={inputStyle} />
                </div>
                <div>
                  <div style={labelStyle}>Email</div>
                  <input value={email} onChange={e => { setEmail(e.target.value); clearSelection(); }} placeholder="name@email.com" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginTop:10, fontSize:12, fontStyle:'italic', color: selectedCustomer ? '#2ecc71' : T.textSub }}>
                {selectedCustomer
                  ? `✓ Using existing customer: ${selectedCustomer.name}`
                  : 'No match selected — a new Lightspeed customer will be created from these details when the ticket is saved.'}
              </div>
            </div>

            {/* Possible matches */}
            <div style={{ background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:8, padding:'10px 12px', alignSelf:'start', minHeight:120 }}>
              <div style={{ ...labelStyle, marginBottom:8 }}>Possible Matches</div>
              {searching && <div style={{ fontSize:12, color:T.textSub, fontStyle:'italic' }}>Searching…</div>}
              {!searching && matches.length === 0 && (
                <div style={{ fontSize:12, color:T.textSub, fontStyle:'italic' }}>
                  {selectedCustomer ? 'Customer selected' : 'Type a name or phone number to search'}
                </div>
              )}
              {!searching && matches.map(c => (
                <div key={c.id} onClick={() => selectMatch(c)}
                  style={{ padding:'7px 8px', borderRadius:6, cursor:'pointer', marginBottom:4,
                    border:'1px solid transparent', transition:'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = isDark ? '#1a2540' : '#f4f4ef'; e.currentTarget.style.borderColor = '#e67e22'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
                  <div style={{ fontSize:13, fontWeight:600, color: isDark ? '#60a5fa' : '#3498db' }}>{c.name}</div>
                  <div style={{ fontSize:11, color:T.textSub }}>{fmtPhone(c.phone) || c.email || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section 2 — ticket details */}
        <div style={{ padding:'16px 22px', borderTop:`1px solid ${T.border}` }}>
          <div style={sectionLabel}>2 · Ticket details</div>
          {selectedCustomer && (
            <div style={{ marginBottom:8 }}>
              <div style={labelStyle}>Customer Item</div>
              <select value={bikeId} onChange={e => pickBike(e.target.value)} disabled={customerBikes === null}
                style={{ ...inputStyle, cursor: customerBikes === null ? 'wait' : 'pointer' }}>
                <option value="">{customerBikes === null ? 'Loading their bikes…'
                  : customerBikes.length ? `New bike (describe it below), or pick one of their ${customerBikes.length}`
                    : 'New bike: no earlier bikes on file for this customer'}</option>
                {(customerBikes || []).map(b => (
                  <option key={b.id} value={b.id}>{[b.label, b.size, b.serial && `SN ${b.serial}`].filter(Boolean).join(' · ')}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1.2fr', gap:8, marginBottom:8, opacity: pickedBike ? 0.65 : 1 }}
            title={pickedBike ? 'Their bike as Lightspeed has it. Edit it from the ticket once it is created.' : undefined}>
            <div>
              <div style={labelStyle}>{pickedBike ? 'Bike Description' : 'Bike Description *'}</div>
              <input value={bikeDescription} onChange={e => setBikeDescription(e.target.value)} readOnly={!!pickedBike} placeholder="e.g. Trek Domane SL5" style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Color</div>
              <input value={color} onChange={e => setColor(e.target.value)} readOnly={!!pickedBike} style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Size</div>
              <input value={size} onChange={e => setSize(e.target.value)} readOnly={!!pickedBike} style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Serial</div>
              <input value={serial} onChange={e => setSerial(e.target.value)} readOnly={!!pickedBike} style={inputStyle} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
            <div>
              <div style={labelStyle}>Status</div>
              <select value={statusId} onChange={e => setStatusId(e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                {(allStatuses || []).filter(s => {
                  const n = s.name.toLowerCase();
                  return !(n.includes('done') && n.includes('paid'));
                }).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <div style={labelStyle}>Tech</div>
              <select value={techId} onChange={e => setTechId(e.target.value)} style={{ ...inputStyle, cursor:'pointer' }}>
                <option value="">Unassigned</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <div style={labelStyle}>Due Date (optional)</div>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Internal Note (optional)</div>
            <textarea value={internalNote} onChange={e => setInternalNote(e.target.value)} rows={2}
              placeholder="Staff-facing note — customer request, symptoms, drop-off details…"
              style={{ ...inputStyle, resize:'vertical', lineHeight:1.5 }} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 22px', borderTop:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontSize:12, color:T.textSub, marginRight:'auto' }}>
            {error
              ? <span style={{ color:'#e74c3c' }}>{error}</span>
              : 'Parts and labor are added after creation — scan or add them from the ticket.'}
          </div>
          <button onClick={onClose} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, padding:'10px 16px', fontSize:13 }}>Cancel</button>
          <button onClick={handleCreate} disabled={!canCreate || saving}
            style={{ ...S.btn(true), background: canCreate ? '#e67e22' : (isDark ? '#243050' : '#d0d0c8'),
              color: canCreate ? '#fff' : T.textSub, padding:'10px 20px', fontSize:13,
              cursor: canCreate ? 'pointer' : 'not-allowed', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creating…' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}